import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";
import { BrandLockup } from "@/components/Brand";

export default function Login() {
    const { login, verify2fa } = useAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const [step, setStep] = useState("password"); // 'password' | '2fa'
    const [mfaToken, setMfaToken] = useState("");
    const [code, setCode] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            const r = await login(email, password);
            if (r?.requires_2fa) {
                setMfaToken(r.mfa_token);
                setStep("2fa");
                return;
            }
            const to = loc.state?.from || "/dashboard";
            nav(to, { replace: true });
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setBusy(false);
        }
    };

    const onVerify = async (e) => {
        e.preventDefault();
        setErr("");
        setBusy(true);
        try {
            await verify2fa(mfaToken, code);
            const to = loc.state?.from || "/dashboard";
            nav(to, { replace: true });
        } catch (e2) {
            setErr(e2.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-background">
            <div className="hidden lg:flex flex-col justify-between p-10 bg-gradient-to-br from-primary via-primary/95 to-[hsl(38_75%_45%)] text-primary-foreground relative overflow-hidden">
                <div className="absolute inset-0 grain opacity-50" />
                <Link to="/" className="relative" data-testid="auth-brand">
                    <BrandLockup size="md" parent={true} className="text-primary-foreground [&_*]:text-primary-foreground" />
                </Link>
                <div className="relative">
                    <h2 className="font-display text-4xl xl:text-5xl font-bold leading-[1.05] tracking-tight max-w-md">
                        Welcome back.
                        <br />
                        <span className="italic font-medium opacity-85">Your vault is exactly where you left it.</span>
                    </h2>
                    <p className="mt-5 text-primary-foreground/85 text-sm max-w-md leading-relaxed">
                        Documents, finances, stories, nominees — encrypted, intact, inheritable.
                    </p>
                </div>
                <div className="relative text-[10px] uppercase tracking-[0.32em] font-semibold text-primary-foreground/70">
                    <a
                        href="https://mythosindiastudio.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="login-mythos-link"
                        className="hover:text-primary-foreground transition-colors"
                    >
                        A MYTHIVA™ platform ↗
                    </a>
                    <span className="mx-2 opacity-60">·</span>
                    audit logged
                </div>
            </div>

            <div className="flex items-center justify-center p-6 sm:p-10">
                {step === "password" ? (
                <form onSubmit={onSubmit} className="w-full max-w-sm" data-testid="login-form">
                    <div className="label-tag mb-2">Sign in</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Enter your vault</h1>
                    <p className="mt-2 text-sm text-muted-foreground">Use your registered email.</p>

                    <div className="mt-8 space-y-4">
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email" type="email" required
                                data-testid="login-email"
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@family.in"
                            />
                        </div>
                        <div>
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password" type="password" required minLength={6}
                                data-testid="login-password"
                                value={password} onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    {err ? (
                        <div data-testid="login-error" className="mt-4 text-sm text-destructive">{err}</div>
                    ) : null}

                    <Button
                        type="submit" disabled={busy}
                        data-testid="login-submit"
                        className="mt-6 w-full rounded-full"
                    >
                        {busy ? "Signing in…" : (
                            <>Sign in <ArrowRight className="ml-2 h-4 w-4" /></>
                        )}
                    </Button>

                    <p className="mt-6 text-sm text-muted-foreground">
                        New here?{" "}
                        <Link to="/register" data-testid="link-to-register" className="text-primary font-semibold hover:underline">
                            Create your vault
                        </Link>
                    </p>
                </form>
                ) : (
                <form onSubmit={onVerify} className="w-full max-w-sm" data-testid="login-2fa-form">
                    <div className="label-tag mb-2">Two-factor authentication</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight">Enter the 6-digit code</h1>
                    <p className="mt-2 text-sm text-muted-foreground">From your authenticator app.</p>
                    <Input
                        data-testid="login-2fa-code"
                        inputMode="numeric" maxLength={6} required autoFocus
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                        placeholder="000000"
                        className="mt-8 font-mono tracking-[0.4em] text-center text-lg"
                    />
                    {err ? <div data-testid="login-2fa-error" className="mt-4 text-sm text-destructive">{err}</div> : null}
                    <Button type="submit" disabled={busy} data-testid="login-2fa-submit" className="mt-6 w-full rounded-full">
                        {busy ? "Verifying…" : (<>Verify <ArrowRight className="ml-2 h-4 w-4" /></>)}
                    </Button>
                    <button
                        type="button"
                        onClick={() => { setStep("password"); setCode(""); setErr(""); }}
                        className="mt-4 text-sm text-muted-foreground hover:text-foreground"
                    >
                        ← Back to password
                    </button>
                </form>
                )}
            </div>
        </div>
    );
}
